import React, { type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import MuiLink from '@mui/material/Link';
import type { SxProps, Theme } from '@mui/material/styles';

import {
  VARIANTS_MAPPING,
  type TypographyVariant,
} from 'shared/atoms/Typography';
import type { BackgroundThemes } from 'shared/const';
import theme from 'shared/themes/default';

type Visibility = 'high' | 'low';

interface ColorParams {
  themeLayout?: BackgroundThemes;
  visibility?: Visibility;
}

const getParagraphColor = ({
  themeLayout,
  visibility,
}: ColorParams): string => {
  if (themeLayout === 'light') return theme.palette.mainDark;
  return visibility === 'high' ? theme.palette.brand : theme.palette.lightPure;
};

const getParagraphHoverColor = ({
  themeLayout,
  visibility,
}: ColorParams): string => {
  if (themeLayout === 'light') return theme.palette.darkDistinct;
  return visibility === 'high' ? theme.palette.lightPure : theme.palette.brand;
};

const getNotParagraphColor = ({
  themeLayout,
  visibility,
}: ColorParams): string => {
  if (themeLayout === 'light') return theme.palette.mainDark;
  return visibility === 'high' ? theme.palette.brand : theme.palette.lightPure;
};

const getNotParagraphActiveColor = ({
  themeLayout,
  visibility,
}: ColorParams): string => {
  if (themeLayout === 'light') return theme.palette.mainDark;
  return visibility === 'high' ? theme.palette.lightPure : theme.palette.brand;
};

interface LinkProps {
  ariaLabel: string;
  children: string | ReactNode;
  disabled?: boolean;
  href?: string;
  paragraph?: boolean;
  sx?: SxProps<Theme>;
  theme?: BackgroundThemes;
  to?: string;
  variant?: TypographyVariant;
  visibility?: Visibility;
}

const Link = ({
  ariaLabel,
  children,
  disabled = false,
  href,
  paragraph = false,
  sx: customSx,
  theme: themeLayout = 'dark',
  to,
  variant = 'paragraph',
  visibility = 'high',
  ...props
}: LinkProps) => {
  if (to && href) {
    throw new Error(
      "Component only accepts one of these 2 parameters ('to' and 'href') at a time. Use 'to' for internal SPA navigation, and 'href' for sending user to an external link"
    );
  }

  const sxLinkBase: SxProps<Theme> = {
    cursor: 'pointer',
    fontWeight: 600,
  };

  const sxLinkParagraph: SxProps<Theme> = {
    color: getParagraphColor({ themeLayout, visibility }),
    textDecorationColor: getParagraphColor({ themeLayout, visibility }),
    '&:hover': {
      color: getParagraphHoverColor({ themeLayout, visibility }),
      textDecoration: themeLayout === 'light' ? 'underline' : 'none',
    },
    '&:focus': {
      outline: `3px solid ${theme.palette.team}`,
      outlineOffset: '1px',
      borderRadius: '1.25rem',
    },
    '&:active': {
      fontWeight: 700,
    },
  };

  const sxLinkNotParagraph: SxProps<Theme> = {
    padding: `${theme.spacing.xs} ${theme.spacing.s}`,
    borderRadius: '1.25rem',
    color: getNotParagraphColor({ themeLayout, visibility }),
    textDecorationColor: getNotParagraphColor({ themeLayout, visibility }),
    '&:hover': {
      backgroundColor:
        themeLayout === 'light'
          ? theme.palette.brand
          : theme.palette.darkDistinct,
      textDecoration: 'none',
    },
    '&:focus': {
      outline: `3px solid ${theme.palette.team}`,
      outlineOffset: '1px',
    },
    '&:active': {
      color: getNotParagraphActiveColor({ themeLayout, visibility }),
      fontWeight: 700,
      textDecoration: 'underline',
    },
  };

  const sxLinkDisabled: SxProps<Theme> = {
    color: theme.palette.disabledDark,
    textDecorationColor: theme.palette.disabledDark,
    cursor: 'auto',
  };

  let sxLink: SxProps<Theme> = { ...sxLinkBase };
  if (disabled) sxLink = { ...sxLink, ...sxLinkDisabled };
  else if (paragraph) sxLink = { ...sxLink, ...sxLinkParagraph };
  else sxLink = { ...sxLink, ...sxLinkNotParagraph };

  // RouterLink from react-router v7 has React version compatibility issues
  // Using any for props type to work around the type constraint issue
  const LinkAdapter = React.forwardRef<HTMLAnchorElement, any>(
    (props: any, ref) => {
      const { to: toProp, ...restProps } = props;
      return <RouterLink ref={ref} to={toProp} {...restProps} />;
    }
  ) as React.ElementType;

  return (
    <MuiLink
      {...(to && !disabled ? { component: LinkAdapter as any } : {})}
      aria-label={ariaLabel}
      href={disabled ? undefined : href}
      {...(to && !disabled ? { to } : {})}
      rel={href ? 'noopener' : undefined}
      sx={{ ...sxLink, ...customSx }}
      target={href ? '_blank' : undefined}
      variant={VARIANTS_MAPPING[variant]}
      {...props}
    >
      {children}
    </MuiLink>
  );
};

export default Link;
export type { LinkProps };
